import { useState, useEffect, useRef, Fragment, useCallback } from 'react'
import { CameraIcon, PhotoIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import { Dialog, DialogPanel, DialogTitle, Transition } from '@headlessui/react'
import { useAuth } from '../hooks/useAuth'
import { getUserDocument, updateUserDocument } from '../lib/userService'
import { UserProfile as UserProfileType } from '../types/user'
import ReviewsSection from './ReviewsSection'
import { convertAvailabilityToGMT, convertAvailabilityFromGMT, getUserTimezone } from '../utils/timezoneUtils'
import { isProfessionalBookingEligible, initiateStripeConnectOnboarding } from '../lib/professionalService'
import { uploadProfileImage, validateImageFile, compressImage } from '../lib/imageUploadService'
import { takePhoto, pickPhoto } from '../services/cameraService'
import { isNativePlatform } from '../utils/platform'
import { Browser } from '@capacitor/browser'

interface TimeSlot {
  start: string
  end: string
}

interface DayAvailability {
  enabled: boolean
  timeSlots: TimeSlot[]
}

interface WeeklyAvailability {
  monday: DayAvailability
  tuesday: DayAvailability
  wednesday: DayAvailability
  thursday: DayAvailability
  friday: DayAvailability
  saturday: DayAvailability
  sunday: DayAvailability
  timezone?: string
}

interface UserProfileProps {
  openAvailability?: boolean
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const DAY_LABELS: Record<string, string> = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
}

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'America/New_York · Eastern' },
  { value: 'America/Chicago', label: 'America/Chicago · Central' },
  { value: 'America/Denver', label: 'America/Denver · Mountain' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles · Pacific' },
  { value: 'America/Anchorage', label: 'America/Anchorage · Alaska' },
  { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu · Hawaii' },
  { value: 'America/Toronto', label: 'America/Toronto · Eastern' },
  { value: 'America/Vancouver', label: 'America/Vancouver · Pacific' },
  { value: 'Europe/London', label: 'Europe/London · GMT' },
  { value: 'Europe/Paris', label: 'Europe/Paris · CET' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin · CET' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo · JST' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai · CST' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata · IST' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney · AEDT' },
]

const EXPERIENCE_OPTIONS = ['0–2', '3–5', '6–10', '10+']

const SWE_SPECIALIZATIONS = [
  'Frontend Engineer',
  'Backend Engineer',
  'Full Stack Engineer',
  'iOS Developer',
  'Android Developer',
  'Mobile Developer',
  'ML Engineer',
  'Data Engineer',
  'DevOps Engineer',
  'Security Engineer',
  'Platform Engineer',
  'Cloud Engineer',
]

// Convert 24h time to 12h display
function to12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${displayH}:${(m || 0).toString().padStart(2, '0')} ${period}`
}

// Generate time options in 30-minute increments (12:00 AM to 11:30 PM)
function generateTimeOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
      options.push({ value, label: to12h(value) })
    }
  }
  return options
}

const TIME_OPTIONS = generateTimeOptions()

// Convert time string "HH:MM" to minutes for comparison
function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

// Check if a new/updated slot would overlap with existing slots on the same day
function hasOverlap(slots: TimeSlot[], newStart: string, newEnd: string, excludeIdx?: number): boolean {
  const newStartMin = timeToMinutes(newStart)
  const newEndMin = timeToMinutes(newEnd)
  for (let i = 0; i < slots.length; i++) {
    if (i === excludeIdx) continue
    const slotStartMin = timeToMinutes(slots[i].start)
    const slotEndMin = timeToMinutes(slots[i].end)
    // Overlap: starts before the other ends AND ends after the other starts
    if (newStartMin < slotEndMin && newEndMin > slotStartMin) {
      return true
    }
  }
  return false
}

// Get valid start time options that won't cause overlap (must be before end time and not overlap other slots)
function getValidStartOptions(slots: TimeSlot[], slotIdx: number, currentEnd: string, currentValue: string): { value: string; label: string }[] {
  const endMin = timeToMinutes(currentEnd)
  const filtered = TIME_OPTIONS.filter(opt => {
    const optMin = timeToMinutes(opt.value)
    if (optMin >= endMin) return false // start must be before end
    return !hasOverlap(slots, opt.value, currentEnd, slotIdx)
  })
  // Always include the current value even if it's not on a 30-min boundary
  if (!filtered.find(o => o.value === currentValue)) {
    filtered.push({ value: currentValue, label: to12h(currentValue) })
    filtered.sort((a, b) => timeToMinutes(a.value) - timeToMinutes(b.value))
  }
  return filtered
}

// Get valid end time options that won't cause overlap (must be after start time and not overlap other slots)
function getValidEndOptions(slots: TimeSlot[], slotIdx: number, currentStart: string, currentValue: string): { value: string; label: string }[] {
  const startMin = timeToMinutes(currentStart)
  const filtered = TIME_OPTIONS.filter(opt => {
    const optMin = timeToMinutes(opt.value)
    if (optMin <= startMin) return false // end must be after start
    return !hasOverlap(slots, currentStart, opt.value, slotIdx)
  })
  // Always include the current value even if it's not on a 30-min boundary
  if (!filtered.find(o => o.value === currentValue)) {
    filtered.push({ value: currentValue, label: to12h(currentValue) })
    filtered.sort((a, b) => timeToMinutes(a.value) - timeToMinutes(b.value))
  }
  return filtered
}

export default function UserProfile({ openAvailability = false }: UserProfileProps) {
  const { user: authUser, loading: authLoading } = useAuth()
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [changedSection, setChangedSection] = useState('')
  const [activeSection, setActiveSection] = useState('identity')

  // Form state — separate from userProfile so we can track dirty state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [specialization, setSpecialization] = useState('')
  const [company, setCompany] = useState('')
  const [bio, setBio] = useState('')
  const [timezone, setTimezone] = useState('')
  const [sessionRate, setSessionRate] = useState('')
  const [experience, setExperience] = useState('')
  const [localAvailability, setLocalAvailability] = useState<WeeklyAvailability | null>(null)

  // UI state
  const [isInitiatingStripeSetup, setIsInitiatingStripeSetup] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [showPhotoOptions, setShowPhotoOptions] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const availabilitySectionRef = useRef<HTMLElement>(null)

  // Load user profile
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!authUser) { setLoading(false); return }
      try {
        const profile = await getUserDocument(authUser.uid)
        if (profile) {
          setUserProfile(profile)
          // Initialize form state
          setFirstName(profile.firstName || '')
          setLastName(profile.lastName || '')
          setSpecialization(profile.title || '')
          setCompany(profile.company || '')
          setBio(profile.bio || '')
          setTimezone(profile.timezone || '')
          setSessionRate(profile.sessionRate?.toString() || '')
          setExperience(profile.experience || '')

          // Initialize availability
          if (profile.availability) {
            const tz = getUserTimezone(profile.timezone)
            const local = convertAvailabilityFromGMT(profile.availability as WeeklyAvailability, tz)
            setLocalAvailability(local)
          } else {
            const defaultDay: DayAvailability = { enabled: false, timeSlots: [] }
            setLocalAvailability({
              monday: defaultDay, tuesday: defaultDay, wednesday: defaultDay,
              thursday: defaultDay, friday: defaultDay, saturday: defaultDay, sunday: defaultDay
            })
          }
        } else {
          setError('User profile not found')
        }
      } catch (err) {
        console.error('Error loading user profile:', err)
        setError('Failed to load user profile')
      } finally {
        setLoading(false)
      }
    }
    if (!authLoading) loadUserProfile()
  }, [authUser, authLoading])

  // Scroll to availability section when navigating from dashboard
  useEffect(() => {
    if (openAvailability && !loading && availabilitySectionRef.current) {
      setTimeout(() => {
        availabilitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    }
  }, [openAvailability, loading])

  // Refresh Stripe status on return
  useEffect(() => {
    if (!authUser || !userProfile) return
    const urlParams = new URLSearchParams(window.location.search)
    const stripeStatus = urlParams.get('stripe')
    if (stripeStatus === 'success' || stripeStatus === 'refresh') {
      setTimeout(async () => {
        try {
          const freshProfile = await getUserDocument(authUser.uid)
          if (freshProfile) setUserProfile(freshProfile)
        } catch (err) {
          console.error('Error refreshing profile:', err)
        }
      }, 1000)
    }
  }, [authUser, userProfile])

  // Scrollspy for jump nav
  useEffect(() => {
    const sections = document.querySelectorAll('.section-anchor')
    const onScroll = () => {
      const y = window.scrollY + 120
      let current = 'identity'
      sections.forEach((s) => {
        if (s instanceof HTMLElement && s.offsetTop <= y) {
          current = s.id
        }
      })
      setActiveSection(current)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [loading])

  // Track changes
  const markChanged = useCallback((section: string) => {
    setHasChanges(true)
    setChangedSection(section)
  }, [])

  // Save all changes
  const handleSave = async () => {
    if (!authUser || !userProfile) return

    setSaving(true)
    setError('')
    setSuccessMessage('')

    try {
      const displayName = `${firstName} ${lastName}`.trim()
      const updateData: any = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName,
        title: specialization,
        company: company.trim(),
        bio: bio.trim(),
        timezone,
        sessionRate: sessionRate ? parseFloat(sessionRate) : undefined,
        specialization,
        experience,
      }

      // Save availability if it changed
      if (localAvailability && timezone) {
        const gmtAvailability = convertAvailabilityToGMT(localAvailability, getUserTimezone(timezone))
        updateData.availability = gmtAvailability
      }

      await updateUserDocument(authUser.uid, updateData)

      // Reload fresh profile
      const freshProfile = await getUserDocument(authUser.uid)
      if (freshProfile) setUserProfile(freshProfile)

      setHasChanges(false)
      setChangedSection('')
      setSuccessMessage('Profile updated successfully!')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (err) {
      console.error('Error saving profile:', err)
      setError(err instanceof Error ? err.message : 'Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  // Discard changes
  const handleDiscard = () => {
    if (!userProfile) return
    setFirstName(userProfile.firstName || '')
    setLastName(userProfile.lastName || '')
    setSpecialization(userProfile.title || '')
    setCompany(userProfile.company || '')
    setBio(userProfile.bio || '')
    setTimezone(userProfile.timezone || '')
    setSessionRate(userProfile.sessionRate?.toString() || '')
    setExperience(userProfile.experience || '')

    if (userProfile.availability) {
      const tz = getUserTimezone(userProfile.timezone)
      setLocalAvailability(convertAvailabilityFromGMT(userProfile.availability as WeeklyAvailability, tz))
    }

    setHasChanges(false)
    setChangedSection('')
    setError('')
    setSuccessMessage('')
  }

  // Stripe setup
  const handleStripeSetup = async () => {
    if (!authUser) return
    setIsInitiatingStripeSetup(true)
    setError('')
    try {
      const result = await initiateStripeConnectOnboarding(authUser.uid, { baseUrl: window.location.origin })
      if (result.error) {
        setError(result.error)
        setIsInitiatingStripeSetup(false)
      } else if (result.url) {
        if (isNativePlatform()) {
          await Browser.open({ url: result.url })
          setIsInitiatingStripeSetup(false)
        } else {
          window.open(result.url, '_blank')
          setIsInitiatingStripeSetup(false)
        }
      }
    } catch {
      setError('Failed to initiate Stripe setup. Please try again.')
      setIsInitiatingStripeSetup(false)
    }
  }

  // Image upload
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !authUser) return
    setIsUploadingImage(true)
    setError('')
    try {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        setError('File type not supported. Please use JPG, PNG, or WebP.')
        setIsUploadingImage(false)
        return
      }
      const compressed = await compressImage(file, 0.7, 2048)
      const validation = validateImageFile(compressed, 2048, allowedTypes)
      if (!validation.isValid) { setError(validation.error || 'Invalid file'); setIsUploadingImage(false); return }

      const result = await uploadProfileImage({ userId: authUser.uid, file: compressed, folder: 'profile-images', maxSizeKB: 2048, allowedTypes })
      if (result.success && result.url) {
        setUserProfile({ ...userProfile!, profileImage: result.url })
        setSuccessMessage('Profile image updated!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to upload image')
      }
    } catch { setError('Failed to upload image.') }
    finally {
      setIsUploadingImage(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleImageUploadClick = () => {
    if (isNativePlatform()) setShowPhotoOptions(true)
    else fileInputRef.current?.click()
  }

  const handleTakePhoto = async () => {
    if (!authUser) return
    setShowPhotoOptions(false)
    setIsUploadingImage(true)
    try {
      const photo = await takePhoto()
      if (!photo) { setIsUploadingImage(false); return }
      const compressed = await compressImage(photo.dataUrl, 0.7, 2048)
      const result = await uploadProfileImage({ userId: authUser.uid, file: compressed })
      if (result.success && result.url) {
        setUserProfile({ ...userProfile!, profileImage: result.url })
        setSuccessMessage('Profile image updated!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else { setError(result.error || 'Failed to upload') }
    } catch { setError('Failed to take photo.') }
    finally { setIsUploadingImage(false) }
  }

  const handlePickPhoto = async () => {
    if (!authUser) return
    setShowPhotoOptions(false)
    setIsUploadingImage(true)
    try {
      const photo = await pickPhoto()
      if (!photo) { setIsUploadingImage(false); return }
      const compressed = await compressImage(photo.dataUrl, 0.7, 2048)
      const result = await uploadProfileImage({ userId: authUser.uid, file: compressed })
      if (result.success && result.url) {
        setUserProfile({ ...userProfile!, profileImage: result.url })
        setSuccessMessage('Profile image updated!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else { setError(result.error || 'Failed to upload') }
    } catch { setError('Failed to pick photo.') }
    finally { setIsUploadingImage(false) }
  }

  const handleRemoveImage = async () => {
    if (!authUser || !userProfile) return
    try {
      await updateUserDocument(authUser.uid, { profileImage: '' } as any)
      setUserProfile({ ...userProfile, profileImage: '' })
      setSuccessMessage('Profile image removed.')
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch { setError('Failed to remove image.') }
  }

  // Notification toggles
  const handleNotificationToggle = async (key: string, enabled: boolean) => {
    if (!authUser || !userProfile) return
    try {
      const updated = {
        bookings: userProfile.notifications.bookings ?? false,
        ratings: userProfile.notifications.ratings ?? false,
        payments: userProfile.notifications.payments ?? false,
        cancellations: userProfile.notifications.cancellations ?? false,
        [key]: enabled
      }
      await updateUserDocument(authUser.uid, { notifications: updated })
      setUserProfile(prev => prev ? { ...prev, notifications: updated } : null)
    } catch { setError('Failed to update notification preferences.') }
  }

  // Availability helpers
  const toggleDay = (day: string) => {
    if (!localAvailability) return
    const current = localAvailability[day as keyof WeeklyAvailability] as DayAvailability
    const updated = { ...current, enabled: !current.enabled }
    if (!current.enabled && current.timeSlots.length === 0) {
      updated.timeSlots = [{ start: '09:00', end: '17:00' }]
    }
    setLocalAvailability({ ...localAvailability, [day]: updated })
    markChanged('Availability')
  }

  const updateSlotTime = (day: string, slotIdx: number, field: 'start' | 'end', value: string) => {
    if (!localAvailability) return
    const dayData = localAvailability[day as keyof WeeklyAvailability] as DayAvailability
    const newSlots = [...dayData.timeSlots]
    const updatedSlot = { ...newSlots[slotIdx], [field]: value }
    // Validate: start must be before end
    if (timeToMinutes(updatedSlot.start) >= timeToMinutes(updatedSlot.end)) return
    // Validate: no overlap with other slots
    if (hasOverlap(newSlots, updatedSlot.start, updatedSlot.end, slotIdx)) return
    newSlots[slotIdx] = updatedSlot
    setLocalAvailability({ ...localAvailability, [day]: { ...dayData, timeSlots: newSlots } })
    markChanged('Availability')
  }

  const addSlot = (day: string) => {
    if (!localAvailability) return
    const dayData = localAvailability[day as keyof WeeklyAvailability] as DayAvailability
    const lastSlot = dayData.timeSlots[dayData.timeSlots.length - 1]
    // Default new slot starts 1 hour after the last slot ends
    const lastEndMin = lastSlot ? timeToMinutes(lastSlot.end) : 9 * 60
    const newStartMin = lastEndMin + 60
    const newEndMin = newStartMin + 120
    // Can't add if exceeds day bounds
    if (newStartMin >= 24 * 60 || newEndMin > 24 * 60) return
    const newStart = `${Math.floor(newStartMin / 60).toString().padStart(2, '0')}:${(newStartMin % 60).toString().padStart(2, '0')}`
    const newEnd = `${Math.floor(newEndMin / 60).toString().padStart(2, '0')}:${(newEndMin % 60).toString().padStart(2, '0')}`
    // Check for overlap before adding
    if (hasOverlap(dayData.timeSlots, newStart, newEnd)) return
    const newSlots = [...dayData.timeSlots, { start: newStart, end: newEnd }]
    setLocalAvailability({ ...localAvailability, [day]: { ...dayData, timeSlots: newSlots } })
    markChanged('Availability')
  }

  const removeSlot = (day: string, slotIdx: number) => {
    if (!localAvailability) return
    const dayData = localAvailability[day as keyof WeeklyAvailability] as DayAvailability
    const newSlots = dayData.timeSlots.filter((_, i) => i !== slotIdx)
    setLocalAvailability({ ...localAvailability, [day]: { ...dayData, timeSlots: newSlots } })
    markChanged('Availability')
  }

  // Initials helper
  const getInitials = () => {
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
    if (firstName) return firstName.substring(0, 2).toUpperCase()
    return 'U'
  }

  const isStripeSetup = userProfile ? isProfessionalBookingEligible({
    id: userProfile.uid,
    stripeAccountId: userProfile.stripeAccountId,
    isStripeOnboarded: userProfile.isStripeOnboarded
  } as any) : false

  const isProfessional = userProfile?.userType === 'professional'

  // Loading
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/40"></div>
        <span className="ml-3 text-white/50">Loading profile...</span>
      </div>
    )
  }

  if (!authUser || !userProfile) {
    return (
      <div className="panel p-6 text-center">
        <p className="text-white/50">Please sign in to view your profile.</p>
      </div>
    )
  }

  const netRate = sessionRate ? Math.round(parseFloat(sessionRate) * 0.85) : 0

  return (
    <div className="max-w-[1280px]">
      {/* Messages */}
      {successMessage && (
        <div className="mb-4 rounded-xl px-4 py-3 text-sm text-green-300 bg-green-500/10 border border-green-500/20">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl px-4 py-3 text-sm text-red-300 bg-red-500/10 border border-red-500/20">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-400 hover:text-red-200">&times;</button>
        </div>
      )}

      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-[44px] leading-[1.05] font-medium tracking-tight-1">
            Profile & <span className="font-serif italic font-normal">settings</span>
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            Everything clients see, what you charge, how you get paid — all in one place.
          </p>
        </div>
      </header>

      {/* Body: jump-nav + form */}
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-10">

        {/* Jump nav (sticky) */}
        <aside className="hidden lg:block">
          <div className="sticky top-6">
            {isProfessional && (
              <>
                <div className="text-[11px] tracking-wide-3 uppercase text-white/50 mb-3 px-2">Public</div>
                <nav className="flex flex-col gap-0.5">
                  <a className={`jump-link ${activeSection === 'identity' ? 'active' : ''}`} href="#identity">Identity</a>
                  <a className={`jump-link ${activeSection === 'about' ? 'active' : ''}`} href="#about">About</a>
                  <a className={`jump-link ${activeSection === 'expertise' ? 'active' : ''}`} href="#expertise">Expertise</a>
                  <a className={`jump-link ${activeSection === 'rates' ? 'active' : ''}`} href="#rates">Session rate</a>
                  <a className={`jump-link ${activeSection === 'availability' ? 'active' : ''}`} href="#availability">Availability</a>
                </nav>
              </>
            )}

            <div className={`text-[11px] tracking-wide-3 uppercase text-white/50 ${isProfessional ? 'mt-7' : ''} mb-3 px-2`}>Account</div>
            <nav className="flex flex-col gap-0.5">
              {!isProfessional && <a className={`jump-link ${activeSection === 'identity' ? 'active' : ''}`} href="#identity">Identity</a>}
              <a className={`jump-link ${activeSection === 'payments' ? 'active' : ''}`} href="#payments">Payments</a>
              <a className={`jump-link ${activeSection === 'notifications' ? 'active' : ''}`} href="#notifications">Notifications</a>
              <a className={`jump-link ${activeSection === 'account' ? 'active' : ''}`} href="#account">Account</a>
            </nav>
          </div>
        </aside>

        {/* Form column */}
        <div className="flex flex-col gap-6 min-w-0">

          {/* =========== IDENTITY =========== */}
          <section id="identity" className="panel p-6 sm:p-8 section-anchor">
            <div className="section-title">
              <span>Identity</span>
              {isProfessional && <span className="text-[11px] tracking-wide-3 uppercase text-white/50">Shown publicly</span>}
            </div>
            <p className="section-sub">The basics clients see in search results and on your public card.</p>

            <div className="grid md:grid-cols-[160px_1fr] gap-7 mt-7 items-start">
              {/* Avatar */}
              <div>
                <span className="label">Photo</span>
                <div className="relative">
                  <div className="w-[160px] h-[160px] rounded-2xl grid place-items-center text-4xl font-semibold relative overflow-hidden"
                       style={{ background: 'linear-gradient(135deg, hsl(0 0% 26%), hsl(0 0% 9%))', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                    {userProfile.profileImage ? (
                      <img src={userProfile.profileImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <>
                        {getInitials()}
                        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none"></div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={handleImageUploadClick} disabled={isUploadingImage} className="btn btn-outline !py-1.5 !px-3 text-xs flex-1 justify-center">
                    {isUploadingImage ? 'Uploading...' : 'Upload'}
                  </button>
                  {userProfile.profileImage && (
                    <button onClick={handleRemoveImage} className="btn btn-ghost !py-1.5 !px-3 text-xs">Remove</button>
                  )}
                </div>
                <p className="hint">PNG or JPG, at least 512×512. Square crop recommended.</p>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageUpload} className="hidden" />
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="label">First name</label>
                  <input className="field" value={firstName} onChange={e => { setFirstName(e.target.value); markChanged('Identity') }} />
                </div>
                <div>
                  <label className="label">Last name</label>
                  <input className="field" value={lastName} onChange={e => { setLastName(e.target.value); markChanged('Identity') }} />
                </div>
                {isProfessional && (
                  <>
                    <div>
                      <label className="label">Specialization</label>
                      <select className="field" value={specialization} onChange={e => { setSpecialization(e.target.value); markChanged('Identity') }}>
                        <option value="">Select specialization</option>
                        {SWE_SPECIALIZATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label">Company</label>
                      <input className="field" value={company} placeholder="Where you work" onChange={e => { setCompany(e.target.value); markChanged('Identity') }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* =========== ABOUT (professionals only) =========== */}
          {isProfessional && (
            <section id="about" className="panel p-6 sm:p-8 section-anchor">
              <div className="section-title">
                <span>About</span>
                <span className="text-[11px] tracking-wide-3 uppercase text-white/50">Shown publicly</span>
              </div>
              <p className="section-sub">A short bio that gives clients a sense of who they'd be talking to.</p>

              <div className="mt-7">
                <label className="label">Short bio</label>
                <textarea
                  className="field"
                  rows={5}
                  value={bio}
                  onChange={e => { setBio(e.target.value); markChanged('About') }}
                  placeholder="Lead with the kind of problems you solve, the teams you've shipped with, and the moments you're most proud of."
                  maxLength={600}
                />
                <div className="flex items-center justify-between mt-2">
                  <p className="hint">Three to five sentences is the sweet spot.</p>
                  <p className="hint"><span className="text-white/70">{bio.length}</span> / 600</p>
                </div>
              </div>
            </section>
          )}

          {/* =========== EXPERTISE (professionals only) =========== */}
          {isProfessional && (
            <section id="expertise" className="panel p-6 sm:p-8 section-anchor">
              <div className="section-title">
                <span>Expertise</span>
                <span className="text-[11px] tracking-wide-3 uppercase text-white/50">Affects search</span>
              </div>
              <p className="section-sub">Your specialty and how long you've been at it.</p>

              <div className="grid md:grid-cols-2 gap-7 mt-7">
                <div>
                  <label className="label">Primary specialty</label>
                  <select className="field" value={specialization} onChange={e => { setSpecialization(e.target.value); markChanged('Expertise') }}>
                    <option value="">Select specialty</option>
                    {SWE_SPECIALIZATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <p className="hint">Clients filter search by specialty first.</p>
                </div>
                <div>
                  <label className="label">Years of experience</label>
                  <div className="flex gap-1.5">
                    {EXPERIENCE_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        onClick={() => { setExperience(opt); markChanged('Expertise') }}
                        className={`chip ${experience === opt ? 'chip-selected' : ''}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  <p className="hint">Displayed as a range, not an exact number.</p>
                </div>
              </div>
            </section>
          )}

          {/* =========== SESSION RATE (professionals only) =========== */}
          {isProfessional && (
            <section id="rates" className="panel p-6 sm:p-8 section-anchor">
              <div className="section-title">
                <span>Session rate</span>
                <span className="text-[11px] tracking-wide-3 uppercase text-white/50">Stripe required</span>
              </div>
              <p className="section-sub">Every Wonderstood session is 30 minutes. Set what you charge for one.</p>

              <div className="grid md:grid-cols-2 gap-5 mt-7 items-start">
                <div>
                  <label className="label">Rate per session</label>
                  <div className="flex">
                    <span className="flex items-center px-3 text-[13px] text-white/50 rounded-l-[10px]"
                          style={{ boxShadow: 'inset 0 0 0 1px hsl(var(--border))', background: 'rgba(255,255,255,0.02)' }}>$</span>
                    <input
                      className="field !rounded-l-none !rounded-r-none"
                      value={sessionRate}
                      onChange={e => { setSessionRate(e.target.value); markChanged('Session rate') }}
                      type="number"
                      min="0"
                      style={{ borderLeft: 'none', borderRight: 'none' }}
                    />
                    <span className="flex items-center px-3 text-xs text-white/50 rounded-r-[10px] whitespace-nowrap"
                          style={{ boxShadow: 'inset 0 0 0 1px hsl(var(--border))', background: 'rgba(255,255,255,0.02)' }}>USD / 30 min</span>
                  </div>
                </div>
                {sessionRate && (
                  <div className="panel-soft p-4">
                    <div className="text-[11px] tracking-wide-3 uppercase text-white/50 mb-2">Clients will see</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-medium tracking-tight-half leading-none">${sessionRate}</span>
                      <span className="text-white/50 text-[13px]">per 30-min session</span>
                    </div>
                    <p className="hint mt-2">Wonderstood takes a flat <span className="text-white/70">15%</span> platform fee — you'd net <span className="text-white/70">${netRate}</span> per session.</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* =========== AVAILABILITY (professionals only) =========== */}
          {isProfessional && (
            <section id="availability" ref={availabilitySectionRef} className="panel p-6 sm:p-8 section-anchor">
              <div className="section-title">
                <span>Availability</span>
                <span className="text-[11px] tracking-wide-3 uppercase text-white/50">Local time</span>
              </div>
              <p className="section-sub">When clients can book you. We'll auto-convert to their timezone.</p>

              <div className="grid md:grid-cols-2 gap-5 mt-7">
                <div>
                  <label className="label">Timezone</label>
                  <select className="field" value={timezone} onChange={e => { setTimezone(e.target.value); markChanged('Availability') }}>
                    <option value="">Select timezone</option>
                    {TIMEZONE_OPTIONS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </div>
              </div>

              {localAvailability && (
                <div className="mt-7">
                  <div className="text-xs font-medium mb-2">Weekly hours</div>
                  <div className="panel-soft px-5">
                    {DAYS.map(day => {
                      const dayData = localAvailability[day]
                      return (
                        <div key={day} className="day-row">
                          <div className={`text-[13px] font-medium ${!dayData.enabled ? 'text-white/50' : ''}`}>
                            {DAY_LABELS[day]}
                          </div>
                          <label className="switch">
                            <input type="checkbox" checked={dayData.enabled} onChange={() => toggleDay(day)} />
                            <span className="slider"></span>
                          </label>
                          {dayData.enabled && dayData.timeSlots.length > 0 ? (
                            <div className="flex flex-col gap-2">
                              {dayData.timeSlots.map((slot, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  <select
                                    className="field !py-1.5 !w-[120px] !text-xs"
                                    value={slot.start}
                                    onChange={e => updateSlotTime(day, idx, 'start', e.target.value)}
                                  >
                                    {getValidStartOptions(dayData.timeSlots, idx, slot.end, slot.start).map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                  <span className="text-white/50">&rarr;</span>
                                  <select
                                    className="field !py-1.5 !w-[120px] !text-xs"
                                    value={slot.end}
                                    onChange={e => updateSlotTime(day, idx, 'end', e.target.value)}
                                  >
                                    {getValidEndOptions(dayData.timeSlots, idx, slot.start, slot.end).map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                  {dayData.timeSlots.length > 1 && (
                                    <button onClick={() => removeSlot(day, idx)} className="btn btn-ghost !px-1.5 !py-1 text-white/40 hover:text-white">
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-white/50 text-xs italic">Off</div>
                          )}
                          {dayData.enabled ? (
                            <button onClick={() => addSlot(day)} className="btn btn-ghost !px-2 !py-1 text-white/40 hover:text-white">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                          ) : (
                            <span></span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* =========== PAYMENTS =========== */}
          {isProfessional && (
            <section id="payments" className="panel p-6 sm:p-8 section-anchor relative overflow-hidden">
              <div className="section-title">
                <span>Payments</span>
                {!isStripeSetup && (
                  <span className="text-[11px] tracking-wide-3 uppercase inline-flex items-center gap-1.5" style={{ color: '#d4b94a' }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#d4b94a' }}></span> Action required
                  </span>
                )}
                {isStripeSetup && (
                  <span className="text-[11px] tracking-wide-3 uppercase inline-flex items-center gap-1.5 text-green-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span> Connected
                  </span>
                )}
              </div>
              <p className="section-sub">Wonderstood routes payments through Stripe Connect — your money lands in your bank, not ours.</p>

              <div className="mt-7 panel-soft p-5 flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0"
                     style={{ background: 'rgba(255,255,255,0.04)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-white/80">
                    <rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{isStripeSetup ? 'Stripe Connected' : 'Connect Stripe'}</div>
                  <div className="text-white/50 text-xs mt-0.5">
                    {isStripeSetup
                      ? 'Your account is verified. Payouts land the next business day.'
                      : 'Verify your identity once. Payouts start the next business day after a session.'
                    }
                  </div>
                </div>
                <button onClick={handleStripeSetup} disabled={isInitiatingStripeSetup} className="btn btn-primary shrink-0">
                  {isInitiatingStripeSetup ? 'Loading...' : isStripeSetup ? 'Stripe Dashboard' : 'Connect Stripe'}
                  <ArrowRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              <p className="hint mt-5">Payout schedule, bank account, and statement descriptor are managed inside Stripe's dashboard.</p>
            </section>
          )}

          {/* =========== NOTIFICATIONS =========== */}
          <section id="notifications" className="panel p-6 sm:p-8 section-anchor">
            <div className="section-title"><span>Notifications</span></div>
            <p className="section-sub">Tune the noise. We default to "less than you'd expect."</p>

            <div className="mt-6 panel-soft px-5">
              {[
                { key: 'bookings', label: 'New booking requests', desc: 'Email + push when a client requests a session.' },
                { key: 'cancellations', label: 'Session reminders & cancellations', desc: '15 minutes and 24 hours before each session.' },
                { key: 'payments', label: 'Payout notifications', desc: 'Whenever Stripe sends money to your bank.' },
                { key: 'ratings', label: 'Reviews & ratings', desc: 'When clients leave feedback after a session.' },
              ].map((item, idx, arr) => (
                <div key={item.key}
                     className="flex items-center justify-between py-4"
                     style={idx !== arr.length - 1 ? { borderBottom: '1px solid hsl(var(--border) / 0.4)' } : undefined}>
                  <div>
                    <div className="text-[13.5px] font-medium">{item.label}</div>
                    <div className="text-white/50 text-xs mt-0.5">{item.desc}</div>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={(userProfile?.notifications as any)?.[item.key] ?? true}
                      onChange={e => handleNotificationToggle(item.key, e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              ))}
            </div>
          </section>

          {/* =========== ACCOUNT =========== */}
          <section id="account" className="panel p-6 sm:p-8 section-anchor">
            <div className="section-title"><span>Account & security</span></div>
            <p className="section-sub">Login details and the things that protect your account.</p>

            <div className="grid md:grid-cols-2 gap-5 mt-7">
              <div>
                <label className="label">Email</label>
                <input className="field" value={userProfile.email} readOnly style={{ opacity: 0.6 }} />
                <p className="hint">Contact support to change your email.</p>
              </div>
              <div>
                <label className="label">Timezone</label>
                <select className="field" value={timezone} onChange={e => { setTimezone(e.target.value); markChanged('Account') }}>
                  <option value="">Select timezone</option>
                  {TIMEZONE_OPTIONS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <label className="label">Account type</label>
              <p className="text-[13.5px] text-white/70">{isProfessional ? 'Engineer' : 'Student'}</p>
            </div>

            {userProfile.emailVerified && (
              <div className="mt-4 flex items-center gap-2 text-xs text-green-400">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                Email verified
              </div>
            )}
          </section>

          {/* =========== REVIEWS (professionals only) =========== */}
          {isProfessional && (
            <section className="panel p-6 sm:p-8">
              <ReviewsSection userId={authUser.uid} />
            </section>
          )}

          {/* =========== SAVE BAR =========== */}
          {hasChanges && (
            <div className="save-bar flex items-center justify-between gap-4 px-5 py-3.5 mt-2">
              <div className="flex items-center gap-3 text-xs">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#d4b94a' }}></span>
                <span className="text-white/90">Unsaved changes</span>
                {changedSection && <span className="text-white/50">in <span className="text-white/70">{changedSection}</span></span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleDiscard} className="btn btn-ghost">Discard</button>
                <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                  {saving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Options Modal (Mobile) */}
      <Transition appear show={showPhotoOptions} as={Fragment}>
        <Dialog as="div" className="relative z-[120]" onClose={() => setShowPhotoOptions(false)}>
          <Transition.Child as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center">
              <Transition.Child as={Fragment}
                enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4" enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-4">
                <DialogPanel className="w-full transform overflow-hidden rounded-t-2xl shadow-2xl transition-all" style={{ background: 'hsl(var(--card))' }}>
                  <div className="p-6">
                    <DialogTitle className="text-lg font-medium text-center mb-4">
                      Update Profile Photo
                    </DialogTitle>
                    <div className="space-y-2">
                      <button onClick={handleTakePhoto} className="w-full flex items-center justify-center gap-3 btn btn-primary !py-3.5 !rounded-xl">
                        <CameraIcon className="h-5 w-5" />
                        Take Photo
                      </button>
                      <button onClick={handlePickPhoto} className="w-full flex items-center justify-center gap-3 btn btn-outline !py-3.5 !rounded-xl">
                        <PhotoIcon className="h-5 w-5" />
                        Choose from Gallery
                      </button>
                      <button onClick={() => setShowPhotoOptions(false)} className="w-full btn btn-ghost !py-3.5 !rounded-xl justify-center">
                        Cancel
                      </button>
                    </div>
                  </div>
                </DialogPanel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
